<script setup>
import BaseDynamicForm from '@/components/base/BaseDynamicForm.vue'

import { computed, ref, defineExpose } from 'vue'
import { useStore } from '@/utils/VuexHelpers'
import {
  INVOICE_STATUS,
  INVOICE_STATUS_OPTIONS
} from '@/constants/invoiceStatus'

const store = useStore()

const dialog = ref(false)
const confirmDialog = ref(false)

const selectedDocket = ref(null)
const pendingFormData = ref(null)

const loadingRevenue = computed(() => store.getters.loadingRevenue)

const fields = computed(() => [
  {
    key: 'invoice_status',
    label: 'Invoice Status',
    value: selectedDocket.value?.invoice_status || INVOICE_STATUS.DRAFT,
    type: 'select',
    items: INVOICE_STATUS_OPTIONS,
    rules: [(v) => !!v || 'Invoice status is required'],
    placeholder: 'Select invoice status...',
    class: 'mb-8',
    menuProps: {
      bottom: true,
      offsetY: true,
      contentClass: 'mt-1',
      dense: true
    }
  }
])

const isLoading = computed(() => {
  const id = selectedDocket.value?.id

  return [
    `update-${id}`,
    `generate-invoice-${id}`,
    `unfreeze-invoice-${id}`
  ].includes(loadingRevenue.value)
})

const formHeader = computed(() => {
  return 'Update Invoice Status'
})

const selectedStatusLabel = computed(() => {
  const status = pendingFormData.value?.invoice_status

  return (
    INVOICE_STATUS_OPTIONS.find((option) => option.value === status)?.text ||
    status
  )
})

const isCurrentlyInvoiced = computed(() => {
  return selectedDocket.value?.invoice_status === INVOICE_STATUS.INVOICED
})

const edit = (item) => {
  selectedDocket.value = item
  dialog.value = true
}

const isRevertingInvoicedStatus = (newStatus) => {
  return isCurrentlyInvoiced.value && newStatus !== INVOICE_STATUS.INVOICED
}

const patchInvoiceStatus = async (newStatus) => {
  return store.dispatch('updateRevenue', {
    payload: {
      id: selectedDocket.value.id,
      invoice_status: newStatus
    }
  })
}

const unfreezeInvoice = async () => {
  return store.dispatch('unfreezeRevenueInvoice', {
    id: selectedDocket.value.id
  })
}

const closeDialog = () => {
  dialog.value = false
  confirmDialog.value = false
  selectedDocket.value = null
  pendingFormData.value = null
}

const saveInvoiceStatus = async (formData) => {
  const newStatus = formData.invoice_status

  let res = null

  if (isCurrentlyInvoiced.value) {
    if (newStatus === INVOICE_STATUS.INVOICED) {
      closeDialog()
      return
    }

    res = await unfreezeInvoice()

    if (res === 'error') return

    // Backend unfreeze resets status to Draft.
    // Only patch again if user selected a different status.
    if (newStatus !== INVOICE_STATUS.DRAFT) {
      res = await patchInvoiceStatus(newStatus)
    }
  } else {
    res = await patchInvoiceStatus(newStatus)
  }

  if (res === 'error') return

  closeDialog()
  store.dispatch('fetchRevenue')
}

const handleFormSubmit = async (formData) => {
  if (isRevertingInvoicedStatus(formData.invoice_status)) {
    pendingFormData.value = formData
    confirmDialog.value = true
    return
  }

  await saveInvoiceStatus(formData)
}

const confirmStatusChange = async () => {
  if (!pendingFormData.value) return

  await saveInvoiceStatus(pendingFormData.value)
}

const cancelStatusChange = () => {
  confirmDialog.value = false
  pendingFormData.value = null
}

defineExpose({
  edit
})
</script>

<template>
  <div>
    <v-dialog v-model="dialog" scrollable max-width="500px">
      <v-card>
        <v-card-title class="d-flex align-center justify-space-between">
          <div class="d-flex align-start flex-column">
            <h6
              class="text-button text-light-blue text-start mb-0"
              style="line-height: 1.5rem"
            >
              {{ formHeader }}
            </h6>

            <div
              v-if="selectedDocket?.docket"
              class="text-body-2 grey--text text--darken-1"
            >
              Docket:
              <span class="font-italic">{{ selectedDocket.docket }}</span>
            </div>

            <div
              v-if="selectedDocket?.destination?.name"
              class="text-body-2 grey--text text--darken-1"
            >
              Customer:
              <span class="font-italic">
                {{ selectedDocket.destination.name }}
              </span>
            </div>
          </div>
        </v-card-title>

        <v-divider class="mt-0 mb-1" />

        <v-card-text>
          <BaseDynamicForm
            :formFields="fields"
            :loading="isLoading"
            :resetOnInputChange="true"
            @submit="handleFormSubmit"
          />
        </v-card-text>
      </v-card>
    </v-dialog>

    <v-dialog v-model="confirmDialog" max-width="480px">
      <v-card>
        <v-card-title class="text-h6"> Reopen invoiced docket? </v-card-title>

        <v-card-text class="text-start">
          This docket is currently marked as <strong>Invoiced</strong>. Changing
          it to <strong>{{ selectedStatusLabel }}</strong> will unlock it for
          further edits and may require the invoice to be adjusted in the
          accounting system. <br /><br />
          Are you sure you want to continue?
        </v-card-text>

        <v-card-actions>
          <v-spacer />

          <v-btn text @click="cancelStatusChange"> Cancel </v-btn>

          <v-btn
            color="warning"
            depressed
            :loading="isLoading"
            @click="confirmStatusChange"
          >
            Yes, update status
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </div>
</template>
